import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { WINDOW_IDS, WINDOWS } from "../data/windows";
import { getDesktopSize } from "../utils/windowGeometry";

const DRAG_THRESHOLD = 4;
const COMPACT_WIDTH = 1600;

const DESKTOP_WINDOW_IDS = WINDOWS.filter(
  (window) => window.showOnDesktop !== false
).map((window) => window.id);

const FALLBACK_SIZE = { width: 88, height: 76 };
const COMPACT_FALLBACK_SIZE = { width: 72, height: 56 };

const rectsIntersect = (a, b) =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;

const normalizeRect = (x1, y1, x2, y2) => {
  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);

  return {
    x,
    y,
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
};

const clampIcon = (x, y, width, height, desktop) => ({
  x: Math.min(Math.max(0, x), Math.max(0, desktop.width - width)),
  y: Math.min(Math.max(0, y), Math.max(0, desktop.height - height)),
});

const isCompactViewport = () => window.innerWidth <= COMPACT_WIDTH;

const fallbackSize = () =>
  isCompactViewport() ? COMPACT_FALLBACK_SIZE : FALLBACK_SIZE;

const getIconSize = (iconEls, id) => {
  const el = iconEls[id];

  if (el) {
    return { width: el.offsetWidth, height: el.offsetHeight };
  }

  return fallbackSize();
};

const getDefaultPositions = (iconEls = {}) => {
  const desktop = getDesktopSize();
  const compact = isCompactViewport();
  const binSize = getIconSize(iconEls, WINDOW_IDS.PROJECTS);
  const binBottom = compact ? 47 : 75;

  return {
    [WINDOW_IDS.BIO]: { x: 10, y: 10 },
    [WINDOW_IDS.IMAGE]: {
      x: compact ? 14 : 17,
      y: compact ? 80 : 120,
    },
    [WINDOW_IDS.PROJECTS]: clampIcon(
      desktop.width - 17 - binSize.width,
      window.innerHeight - binBottom - binSize.height,
      binSize.width,
      binSize.height,
      desktop
    ),
  };
};

const clampPositions = (positions, iconEls) => {
  const desktop = getDesktopSize();
  const next = {};

  DESKTOP_WINDOW_IDS.forEach((id) => {
    const size = getIconSize(iconEls, id);
    const pos = positions[id] || getDefaultPositions(iconEls)[id];
    next[id] = clampIcon(pos.x, pos.y, size.width, size.height, desktop);
  });

  return next;
};

const clampGroupDelta = (origins, iconEls, dx, dy) => {
  const desktop = getDesktopSize();
  let minDx = -Infinity;
  let maxDx = Infinity;
  let minDy = -Infinity;
  let maxDy = Infinity;

  Object.keys(origins).forEach((id) => {
    const size = getIconSize(iconEls, id);
    const origin = origins[id];

    minDx = Math.max(minDx, -origin.x);
    maxDx = Math.min(maxDx, desktop.width - size.width - origin.x);
    minDy = Math.max(minDy, -origin.y);
    maxDy = Math.min(maxDy, desktop.height - size.height - origin.y);
  });

  const clampAxis = (value, min, max) => {
    const low = Number.isFinite(min) ? min : 0;
    const high = Number.isFinite(max) ? max : 0;

    if (low > high) {
      return 0;
    }

    return Math.min(Math.max(value, low), high);
  };

  return {
    dx: clampAxis(dx, minDx, maxDx),
    dy: clampAxis(dy, minDy, maxDy),
  };
};

const toLocalPoint = (desktopEl, clientX, clientY) => {
  const rect = desktopEl.getBoundingClientRect();

  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
};

const idsFromMarquee = (marquee, positions, iconEls, baseSelected, additive) => {
  const hits = DESKTOP_WINDOW_IDS.filter((id) => {
    const pos = positions[id];
    const size = getIconSize(iconEls, id);

    return (
      pos &&
      rectsIntersect(marquee, {
        x: pos.x,
        y: pos.y,
        width: size.width,
        height: size.height,
      })
    );
  });

  if (!additive) {
    return hits;
  }

  const merged = new Set(baseSelected);

  hits.forEach((id) => merged.add(id));

  return Array.from(merged);
};

export const useDesktopIcons = (open) => {
  const desktopRef = useRef(null);
  const iconEls = useRef({});
  const iconRefCallbacks = useRef({});
  const sessionRef = useRef(null);
  const positionsRef = useRef(null);
  const selectedRef = useRef([]);

  const [positions, setPositions] = useState(getDefaultPositions);
  const [selectedIds, setSelectedIds] = useState([]);
  const [marquee, setMarquee] = useState(null);
  const [draggingIds, setDraggingIds] = useState([]);

  positionsRef.current = positions;
  selectedRef.current = selectedIds;

  const setIconRef = useCallback((id) => {
    if (!iconRefCallbacks.current[id]) {
      iconRefCallbacks.current[id] = (el) => {
        if (el) {
          iconEls.current[id] = el;
        } else {
          delete iconEls.current[id];
        }
      };
    }

    return iconRefCallbacks.current[id];
  }, []);

  const endSession = useCallback(() => {
    sessionRef.current = null;
    setMarquee(null);
    setDraggingIds([]);
    document.body.style.userSelect = "";
  }, []);

  useLayoutEffect(() => {
    setPositions(getDefaultPositions(iconEls.current));
  }, []);

  useEffect(() => {
    const onResize = () => {
      setPositions((prev) => clampPositions(prev, iconEls.current));
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onMove = (event) => {
      const session = sessionRef.current;

      if (!session) {
        return;
      }

      if (session.type === "marquee") {
        const point = toLocalPoint(session.desktop, event.clientX, event.clientY);
        const nextMarquee = normalizeRect(
          session.startX,
          session.startY,
          point.x,
          point.y
        );

        if (nextMarquee.width < DRAG_THRESHOLD && nextMarquee.height < DRAG_THRESHOLD) {
          return;
        }

        setMarquee(nextMarquee);
        setSelectedIds(
          idsFromMarquee(
            nextMarquee,
            positionsRef.current,
            iconEls.current,
            session.baseSelected,
            session.additive
          )
        );
        return;
      }

      const dx = event.clientX - session.startX;
      const dy = event.clientY - session.startY;

      if (!session.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
          return;
        }

        session.moved = true;
        setDraggingIds(Object.keys(session.origins));
      }

      const delta = clampGroupDelta(session.origins, iconEls.current, dx, dy);
      const next = { ...positionsRef.current };

      Object.keys(session.origins).forEach((id) => {
        next[id] = {
          x: session.origins[id].x + delta.dx,
          y: session.origins[id].y + delta.dy,
        };
      });

      setPositions(next);
    };

    const onUp = () => {
      const session = sessionRef.current;

      if (!session) {
        return;
      }

      if (session.type === "icon" && !session.moved) {
        if (session.additive) {
          setSelectedIds((prev) =>
            session.wasSelected
              ? prev.filter((id) => id !== session.id)
              : prev.includes(session.id)
              ? prev
              : [...prev, session.id]
          );
        } else {
          setSelectedIds([session.id]);
        }
      }

      endSession();
    };

    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);

    return () => {
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", onUp, true);
      window.removeEventListener("pointercancel", onUp, true);
    };
  }, [endSession]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      setSelectedIds([]);
      endSession();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [endSession]);

  const onDesktopPointerDown = useCallback((event) => {
    if (event.button !== 0 || event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();

    const additive = event.ctrlKey || event.metaKey;
    const point = toLocalPoint(event.currentTarget, event.clientX, event.clientY);

    if (!additive) {
      setSelectedIds([]);
    }

    if (event.pointerId != null) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    document.body.style.userSelect = "none";
    sessionRef.current = {
      type: "marquee",
      desktop: event.currentTarget,
      startX: point.x,
      startY: point.y,
      additive,
      baseSelected: additive ? [...selectedRef.current] : [],
    };
  }, []);

  const onIconPointerDown = useCallback((event, id) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.detail >= 2) {
      return;
    }

    const additive = event.ctrlKey || event.metaKey;
    const wasSelected = selectedRef.current.includes(id);
    let nextSelected = selectedRef.current;

    if (additive) {
      if (!wasSelected) {
        nextSelected = [...selectedRef.current, id];
        setSelectedIds(nextSelected);
      }
    } else if (!wasSelected) {
      nextSelected = [id];
      setSelectedIds(nextSelected);
    }

    const origins = {};

    nextSelected.forEach((selectedId) => {
      origins[selectedId] = {
        ...(positionsRef.current[selectedId] || { x: 0, y: 0 }),
      };
    });

    if (event.pointerId != null) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    document.body.style.userSelect = "none";
    sessionRef.current = {
      type: "icon",
      id,
      startX: event.clientX,
      startY: event.clientY,
      additive,
      wasSelected,
      origins,
      moved: false,
    };
  }, []);

  const onIconDoubleClick = useCallback(
    (event, id) => {
      event.preventDefault();
      open(id);
    },
    [open]
  );

  return {
    desktopRef,
    positions,
    selectedIds,
    marquee,
    draggingIds,
    setIconRef,
    onDesktopPointerDown,
    onIconPointerDown,
    onIconDoubleClick,
  };
};
